import React from "react";
import {
	SectionHeading,
	FieldsContainer,
	SettingsFormContainer,
	SaveSettingButton,
	FieldLabel,
	SectionDivider,
} from "../../styles/common.styles";
import StatusMessageField from "../../components/StatusMessageField";
import SettingsSnackbar from "../../components/SettingsSnackbar";

import Autocomplete, {
	AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { AlertColor } from "@mui/material/Alert";
import { Typography } from "@mui/material";

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

interface Props {
	handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	settings: Record<string, string | boolean | string[]>;
	setSettings: React.Dispatch<
		React.SetStateAction<Record<string, string | boolean | string[]>>
	>;
	notice: { type: AlertColor; message: string };
	showNotice: boolean;
	setShowNotice: React.Dispatch<React.SetStateAction<boolean>>;
	loading: boolean;
}

const OrderNotiSettings = ({
	handleSubmit,
	settings,
	notice,
	showNotice,
	setSettings,
	setShowNotice,
	loading,
}: Props) => {
	const { orderStatuses } =
		// @ts-ignore
		window.adminUiAppContext || {};

	return (
		<SettingsFormContainer>
			<form onSubmit={(e) => handleSubmit(e)}>
				<SettingsSnackbar
					notice={notice}
					showNotice={showNotice}
					setShowNotice={setShowNotice}
				/>
				<FieldsContainer>
					<SectionHeading variant='h6'>
						Order Notification status messages settings
					</SectionHeading>
					<FieldLabel variant='subtitle2'>Include Statuses</FieldLabel>
					{settings["include-statuses"] === undefined ||
					(settings["include-statuses"] as string[]).length < 0 ? (
						<Skeleton variant='rectangular' width='100%'>
							<TextField />
						</Skeleton>
					) : (
						<Autocomplete
							multiple
							value={(settings["include-statuses"] as string[]) || []}
							options={Object.keys(orderStatuses)}
							getOptionLabel={(option: any) =>
								(option[0].toUpperCase() + option.substring(1))
									.split("-")
									.join(" ")
							}
							style={{ width: "100%" }}
							renderInput={(params: AutocompleteRenderInputParams) => (
								<TextField
									{...params}
									name='include-statuses'
									variant='outlined'
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{(option[0].toUpperCase() + option.substring(1))
										.split("-")
										.join(" ")}
								</li>
							)}
							onChange={(e, newVal: string[] | null) => {
								const includedVals = newVal;
								if (includedVals) {
									setSettings({
										...settings,
										"include-statuses": includedVals,
									});
								}
							}}
						/>
					)}

					<SectionDivider />

					<StatusMessageField
						isDefaultMessage
						settings={settings}
						setSettings={setSettings}
						fieldLabel='Default Message'
						statusSlug='default-message'
					/>

					{!Object.keys(settings).length ? (
						<Box sx={{ mt: 3 }}>
							<Skeleton width='20%'>
								<Typography>.</Typography>
							</Skeleton>
							<Skeleton
								variant='rectangular'
								height='22px'
								width='25%'
								sx={{ mt: 1 }}
							/>
							<Skeleton variant='rectangular' width='100%' sx={{ mt: 0.5 }}>
								<TextField multiline minRows={2} />
							</Skeleton>
						</Box>
					) : (
						Object.entries(orderStatuses).map(([statusSlug, statusLabel]) => {
							const option = {
								slug: statusSlug,
								label: statusLabel,
							};

							const isIncluded = (
								(settings["include-statuses"] as string[]) || []
							).some((statusSlug) => statusSlug === option.slug);

							if (isIncluded) {
								return (
									<>
										<StatusMessageField
											settings={settings}
											setSettings={setSettings}
											fieldLabel={statusLabel as string}
											statusSlug={statusSlug}
											key={statusSlug}
										/>
									</>
								);
							} else {
								return null;
							}
						})
					)}
				</FieldsContainer>

				<SaveSettingButton
					loading={loading}
					size='large'
					variant='contained'
					type='submit'>
					Save settings
				</SaveSettingButton>
			</form>
		</SettingsFormContainer>
	);
};

export default OrderNotiSettings;
